/**
 * Default settings blobs for the Tx Presenter Settings UI.
 * Each section is keyed by its short-name (matching the backend's tx-presenter.js
 * TX_PRESENTER_SECTIONS). Frontend uses these when:
 *   - the backend returns null for a section (practice hasn't saved yet)
 *   - the user resets a section to defaults
 *
 * Defaults seeded from the Claude Design package (`Treatment Presenter/`).
 */

export const TX_PRESENTER_DEFAULTS = {
  general: {
    practiceName: '',           // resolved from teamio.sites.Name when blank
    practiceTagline: '',         // e.g., "FAMILY DENTISTRY OF EMERSON"
    practiceLogoUrl: '',         // future — for now, a colored circle with first letter
    defaultView: 'region',        // 'region' | 'itemized' (opens first when both enabled)
    // Which patient views the practice exposes. At least one must stay on.
    // When only one is enabled the patient sees no view switcher.
    enabledViews: { region: true, itemized: true },
    plainLanguageEnabled: true,  // toggles AI plain-language labels in patient view
    // Slice 12 — after a patient signs, walk them through incomplete consent
    // forms + an optional payment in a step-by-step popup (and show a pre-sign
    // nudge for unsigned consents). Defaults ON; only explicit false disables.
    patientGuidedFlowEnabled: true,
    // OD imaging — where signed PDFs land in OD when teamio-exe relays them.
    // Picked from a dropdown of definition Category=18 rows in Settings → General.
    imagingCategoryDefNum: null,

    // Tick OD's "Signed" box on the treatment plan when a patient signs (2026-09-09).
    // Defaults OFF and ONLY an explicit true enables it: the write REPLACES whatever
    // sits in OD's treatplan.Signature, including a real signature captured inside
    // Open Dental, and we cannot undo that. Until this toggle existed every site with
    // an exe got the write; only Harrisburg Smiles and Tulip Tree Dental Care asked
    // for it and they are seeded true. The chart note ships either way.
    // Backend twin: tx-presenter-service/lib/od-writeback.js resolveOdSignatureWriteback.
    odSignatureWriteback: false,

    // MASTER toggle for patient comments / questions on treatment plans
    // ("Allow Comments / Questions on Treatment Plans", 2026-08-28).
    //
    // Default OFF everywhere. A real JSON boolean — this blob is exempt from the
    // 'TRUE'/'FALSE' string convention, so absent / null / 'true' / 1 all resolve
    // to OFF (see the store getter commentsFeatureEnabled and the SET_PATIENT_VIEW_DATA
    // mutation, both of which compare === true).
    //
    // EFFECTIVE state is stored `true` AND the site has a verified sender email
    // (GENERAL_VERIFIED_EMAILS). Without a sender the office can never be notified
    // of a question and the patient can never be told there is an answer, so the
    // whole feature is greyed out and the server refuses to store `true`.
    //
    // Server twin: tx-presenter-service/lib/comments-gate.js — that is the real
    // gate; this default only decides what the UI shows.
    //
    // Per the repo rule for gated settings, a default-OFF feature needs BOTH
    // absent=OFF here (covers every existing site) AND an explicit `false` written
    // by the onboarding seeder (teamio-admin/seed-tx-presenter.js buildGeneralBlob),
    // so a newly onboarded office can SEE the setting instead of inferring it.
    commentsEnabled: false,

    // Slice 8b — SUB-FLAG under `commentsEnabled`: when the master toggle is off
    // there are no patient questions to answer, so this is unreachable regardless
    // of its value (the Settings UI hides the card, and the backend gate refuses
    // the post before AI is ever consulted). Governs whether an AI assistant drafts
    // replies to questions patients DO ask.
    // Defaults ON for the trial window. Backend ALSO honors the platform-wide
    // flag (settings row SiteID='__GLOBAL__', Property='TX_PRESENTER_AI_QA_ENABLED'),
    // which can disable AI for everyone in one SQL statement at trial-end.
    // See "Feature Lifecycle Notes — AI Q&A on Comments" in tx-presenter.md.
    aiCommentsEnabled: true,

    // Slice 8d-1 — AI delivery mode (auto-send vs. hold for office review).
    //   'review' = AI drafts held until the office approves them in the Builder
    //   'auto'   = AI reply visible to the patient immediately
    // Default flipped to 'review' on 2026-08-04 after two grounding bugs put a
    // false "not on your plan" and a 3.8x-overstated fee in front of a patient
    // with no human in the loop. Keep this in sync with resolveAiReplyMode in
    // tx-presenter-service/tx-presenter-comments.js — a mismatch makes the
    // patient view promise an instant answer the backend then holds.
    aiReplyMode: 'review',

    // Slice 8d — free-form list of email addresses that get notified when a
    // patient asks a question on the Comments thread. "Whoever sees it first
    // responds" — not employee-specific, just a shared inbox-style list.
    // Empty array = notifications skip silently. The patient also gets a
    // ping (SMS or email matching the original send channel) when the
    // office replies, but that's automatic and not gated by this list.
    notificationEmails: [],

    // The office address a PATIENT sees and replies to when Teamio sends a plan on
    // the practice's behalf (the platform sender). Its own key, deliberately —
    // until 2026-08-30 this was `notificationEmails[0]`, which is a different thing
    // with a different audience: that list is INTERNAL staff alerting ("who gets
    // told a patient commented"), it is optional, and it has no ordering
    // affordance — so putting a dentist's address before the front desk's silently
    // redirected every patient reply. One public address, one internal list.
    //
    // Resolution (utils/resolveChannels.js `resolveOfficeReplyEmail`, mirrored on
    // the backend): this key first, then `notificationEmails[0]` as a permanent
    // legacy fallback, then nothing — at which point a platform-sender site cannot
    // email at all and both the resolver and the save refuse.
    officeReplyEmail: '',

    // Slice 7d — Post-sign email template. Sent with the signed PDF attached
    // to the patient's email-on-file (if any) immediately after they sign.
    // Will migrate to the dedicated Delivery section in a future Slice 7e pass;
    // lives in General for now since Delivery section isn't built yet.
    // Placeholders: {patientFirstName}, {practiceName}
    postSignEmailSubject: 'Your treatment plan from {practiceName}',
    postSignEmailBody: [
      'Hi {patientFirstName},',
      '',
      'Thank you for reviewing your treatment plan today. A signed copy is attached for your records.',
      '',
      'If you have any questions, please reach out to our office.',
      '',
      '— {practiceName}',
    ].join('\n'),
  },

  // Practice "house style" — how plans look to the patient by default. A new
  // plan inherits these; the TC can still override any of them per-patient in
  // the Builder/Present Mode (those writes land in the plan's
  // Annotations.pricingDisplay). `enabledViews` + `defaultView` moved here from
  // `general` (the Presentation settings section owns all "how it looks" config
  // now); the `general` copies are kept as a soft-migration fallback.
  presentation: {
    enabledViews: { region: true, itemized: true },
    defaultView: 'region',         // 'region' | 'itemized' (opens first when both enabled)
    // Per-patient pricingDisplay defaults. Values match the historical
    // hardcoded behavior, so nothing changes until a practice edits them.
    displayDefaults: {
      // How much money each view shows:
      //   'full'  — per-procedure fees (gated by `layers`) + Plan Summary card
      //   'total' — no per-row fees; only the Plan Summary total
      //   'none'  — no money anywhere in that view
      pricing: { region: 'full', itemized: 'full' },
      // Legacy per-view on/off. Superseded by `pricing`, still written and read
      // so cached patient bundles keep resolving — see resolveDisplay.js.
      viewVisibility: { region: true, itemized: true },
      // Independent fee-breakdown lines (any combination can show). Global on
      // purpose: these say WHICH sub-lines appear wherever a view is 'full',
      // not which views show money — that's `pricing`.
      layers: { netFee: true, standardFee: true, insuranceEstimate: true, membership: true },
      // Itemized-view grouping by OD priority + the header word + per-group subtotals.
      itemizedGroupByPriority: false,
      itemizedGroupLabel: 'Priority',
      itemizedGroupSubtotals: true,
      // Whether diagnostic codes (exams, X-rays — D0xxx) show by default.
      showDiagnostic: true,
      // Whether the guarantor's OD account balance / credit shows as a Plan
      // Summary line (folded into the Estimated Patient Portion). Default ON.
      showBalance: true,
      // Whether the patient's attached photos render on a plan — the camera
      // icon beside a tooth number and the "Photos of Planned Teeth" gallery.
      // Default ON: attaching a photo is already the deliberate act, so an
      // office that took one means the patient to see it. Photos are
      // patient-level, so this is the only per-plan gate.
      showPhotos: true,
    },
    // Insurance disclaimer + acknowledgement checkboxes shown above the signature
    // (Present Mode, patient link, signed PDF). PRACTICE-WIDE ONLY - there is no
    // per-plan override and it is NOT part of displayDefaults / resolveDisplay.
    // Default OFF and absent => OFF: no lines, paragraph hidden, so every surface
    // behaves exactly as before until an office edits the card. The seeder STRIPS
    // this key so one office's legal text never travels to a new site.
    // Shape + caps + resolve rules: utils/disclaimer.js (twinned with the backend).
    // Plan: docs/plans/2026-10-20-tx-presenter-disclaimer-plan.md
    disclaimer: { showText: false, text: '', acknowledgements: [] },
  },

  payment: {
    // Patient-facing button language for the "I'm interested" call-to-action on options
    buttonLanguage: 'I\'m interested',
    helperNote: 'Pick the option that works best for you. We\'ll follow up to finalize.',
    // Practice-wide payment options — TC tweaks per-patient in the Builder.
    //
    // Per-option flexibility flags (all default off; backfill-safe):
    //   downpaymentEnabled — when on AND type !== 'discount', the Builder AND
    //                        Present Mode show a "Pay today (optional)" input
    //                        that subtracts from the split/finance math. The
    //                        AMOUNT is per-patient (like feeAmount) and lives on
    //                        the option as `downpayment` — NOT here; this blob is
    //                        practice-wide. Ask canTakeDownpayment() rather than
    //                        re-deriving the condition: a hand-written copy once
    //                        gated on breakdown kind === 'split', which hid the
    //                        input for every FINANCING option (prod 2026-08-04).
    //   feeEnabled         — when on, exposes an Additional Fee. The amount
    //                        is per-patient (Builder), shown read-only in
    //                        Present Mode / patient view / signed PDF.
    //   feeDesc / feeUnit  — patient-facing label + '$' or '%' interpretation.
    //
    // Finance options are multi-tier: deferredMonths[] (estimated at 0% over
    // the deferral) + aprMonths[] sharing one `apr`. Each tier renders as a
    // row with an auto-calculated estimated monthly payment. Legacy single-
    // term options (just months+apr) are backfilled into a single tier.
    //
    //   provider — which third-party lender actually backs a finance option.
    //              null = none (the terms below are the practice's own hand-typed
    //              estimate, which is all this has ever been). 'cherry' = the
    //              office has connected a Cherry account, so a real eligibility
    //              lookup and application become possible for this option.
    //              Only meaningful on type === 'finance'; backfilled to null like
    //              odPayTypeDefNum, so existing blobs need no migration.
    //              ⛔ With 'cherry' the typed deferredMonths/aprMonths/apr below are NOT used:
    //              patients see Cherry's real plans for their amount, or no figures at all
    //              (never the typed tiers). At most ONE option may be 'cherry'.
    //   cherryHiddenPlans — only on a Cherry-backed option: the plans the practice switched OFF in
    //              Settings -> "Your Cherry plans", as `${term}-${PERIOD}` keys ("12-MONTHLY",
    //              "3-BIWEEKLY"). ABSENT or [] = every plan Cherry offers. Display-only: patients
    //              can still be offered a hidden plan inside Cherry's own application (turn it off
    //              in the Cherry portal to stop that). Deliberately NOT defaulted or backfilled
    //              (an absent key is valid and must not dirty existing blobs); Settings writes it
    //              explicitly, even as [], only on options that are Cherry-backed on Save.
    //              Re-overlaid from live settings (META x3). A SEPARATE per-patient list,
    //              `cherryHiddenPlansPatient`, lives only in a patient's PaymentOptionOverrides.
    //   cherryTermMonths — LEGACY allow-list (positive ints matched against each plan's term),
    //              replaced by cherryHiddenPlans. No longer written (Settings drops it on Save);
    //              still honoured by cherryPlansFor if a stored blob carries one.
    options: [
      {
        id: 'prepay',
        title: 'Pay in full',
        type: 'discount',
        desc: '{discount}% off when paid in full at first visit (cash, check, or debit).',
        enabled: true,
        recommended: false,
        discount: 5,
        terms: '$100 deposit required. Cannot combine with other plans.',
        downpaymentEnabled: false, // UI disables this for type==='discount'
        feeEnabled: false,
        feeDesc: '',
        feeUnit: '$',
      },
      {
        id: 'half',
        title: 'Half & Half',
        type: 'split',
        desc: '50% at first visit, 50% at treatment delivery.',
        enabled: true,
        recommended: true,
        splits: 2,
        terms: '$100 non-refundable deposit.',
        downpaymentEnabled: false,
        feeEnabled: false,
        feeDesc: '',
        feeUnit: '$',
      },
      {
        id: 'inhouse',
        title: 'In-house plan',
        type: 'inhouse',
        desc: '{splits} equal payments across your treatment.',
        enabled: true,
        recommended: false,
        splits: 3,
        terms: '$100 deposit. No interest.',
        downpaymentEnabled: false,
        feeEnabled: false,
        feeDesc: '',
        feeUnit: '$',
      },
      {
        id: 'finance',
        title: 'No Down Payment',
        type: 'finance',
        desc: 'Financing through CareCredit — choose the term that fits your budget.',
        enabled: false,
        recommended: false,
        deferredMonths: [6, 12],   // shown as "Deferred Interest" (estimate at 0%)
        aprMonths: [24, 36, 48, 60], // shown as "Reduced APR @ {apr}%"
        apr: 14.9,                 // shared APR for the reduced-APR group
        provider: null,            // null | 'cherry' — see the note above the array
        terms: 'For patients with approved credit. No down payment required. Interest for deferred options is 0% if paid in full within the promotional period. Estimated interest; actual may vary. Subject to credit approval.',
        downpaymentEnabled: false,
        feeEnabled: false,
        feeDesc: '',
        feeUnit: '$',
      },
    ],
  },

  // Slice 7e — Send-link templates. SMS body + email subject/body that the TC
  // sees pre-filled (and can override per-send) when sending a plan link to a
  // patient. Placeholders: {patientFirstName}, {practiceName}, {planLink}.
  delivery: {
    smsTemplate: 'Hi {patientFirstName}, your treatment plan from {practiceName} is ready to review: {planLink}',
    emailSubject: 'Your treatment plan from {practiceName}',
    emailBody: [
      'Hi {patientFirstName},',
      '',
      '{practiceName} has a treatment plan ready for you to review.',
      '',
      'Tap here to see it: {planLink}',
      '',
      'If anything looks unclear, please reach out to our office.',
      '',
      '— {practiceName}',
    ].join('\n'),
  },

  // Slice 11 Phase A — Consent Forms mapping. Each row maps one or more
  // procedure codes to a single Teamio Form (FormType='OTHER'). Bundling on
  // send (Phase B) reads this and surfaces the resolved forms in the
  // patient view's "Consent forms to fill out" callout.
  //
  // Schema: { mappings: [{ codes: [codeNum, ...], formIDs: [formIDForWeb] }, ...] }
  // - `codes` is an array of CodeNum (numeric) for multi-select chip support.
  // - `formIDs` is an array even though the UI is single-select, so the
  //   schema stays forward-compatible if we ever revisit "many forms per
  //   row" (deduped at resolution time anyway).
  // - Empty `mappings` = feature configured but no codes mapped yet.
  consent: {
    mappings: [],
  },

  // Stubs for sections built in later slices.
  // VESTIGIAL (2026-08-28): Slice 8 shipped its settings inside `general`, not
  // here. The master toggle is `general.commentsEnabled`, with `aiCommentsEnabled`,
  // `aiReplyMode` and `notificationEmails` nested under it. There is no Comments
  // section in the Settings sidebar and the backend's TX_PRESENTER_SETTINGS_COMMENTS
  // row is never written. Kept only so the section map stays shape-complete —
  // do NOT add the toggle here, it would split the feature across two blobs.
  comments: null,            // Slice 8 (vestigial — see above)
  procedure_content: null,   // Slice 6 deferred Settings UI (per-plan editing covers it)
  addons: null,              // Phase 2 candidate
};

/**
 * Section metadata for the Settings page sidebar (order, label, slice owner).
 * `enabled: false` sections show in the nav but are disabled with a "Slice N"
 * badge so the user understands what's coming.
 */
export const TX_PRESENTER_SECTION_META = [
  { id: 'general', label: 'General', icon: 'cog', enabled: true },
  { id: 'presentation', label: 'Presentation', icon: 'sliders', enabled: true },
  { id: 'payment', label: 'Payment Options', icon: 'credit-card', enabled: true },
  { id: 'delivery', label: 'Delivery', icon: 'paper-plane-o', enabled: true },
  { id: 'consent', label: 'Consent Forms', icon: 'file-text-o', enabled: true },
];
